import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Only the SUFFIX morphs. "D" stays anchored.
const SUFFIXES = ["éménagement", "épannage", "ébarras", "isponibilité", "evis gratuit"];

export default function Hero() {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % SUFFIXES.length), 2000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 pb-24 md:pt-44 md:pb-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center mb-10 md:mb-14"
        >
          <img
            src="/assets/logo-des-full.png"
            alt="D.E.S Déménagements"
            data-testid="hero-logo"
            className="h-32 sm:h-44 md:h-56 lg:h-64 w-auto drop-shadow-[0_8px_40px_rgba(255,40,0,0.18)]"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-8 text-center"
        >
          Express · Strasbourg · Grand Est · Europe
        </motion.div>

        <h1
          data-testid="hero-d-morph"
          className="font-display font-light tracking-tighter leading-[1.04] text-[clamp(2.75rem,9vw,8.5rem)] text-black"
        >
          <span className="inline-flex items-baseline whitespace-nowrap">
            <span className="text-black">D</span>
            <span className="relative inline-block overflow-hidden h-[1.05em] align-baseline">
              <AnimatePresence mode="wait">
                <motion.span
                  key={i}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  exit={{ y: "-110%" }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="block whitespace-nowrap text-[#FF2800]"
                >
                  {SUFFIXES[i]}
                </motion.span>
              </AnimatePresence>
            </span>
          </span>
          <span className="block text-neutral-400">Express Strasbourg.</span>
        </h1>

        <div className="mt-12 md:mt-16 grid md:grid-cols-12 gap-8 items-end">
          <p className="md:col-span-7 text-lg md:text-xl text-neutral-600 leading-relaxed max-w-2xl font-body">
            Déménagement, débarras et interventions express dans tout le Grand Est —
            et partout en Europe. Un service rigoureux, ponctuel, et un devis
            <span className="text-black font-medium"> gratuit</span> sous 24 heures.
          </p>

          <div className="md:col-span-5 flex flex-wrap items-center gap-4 md:justify-end">
            <Link
              to="/devis"
              data-testid="hero-cta-devis"
              className="btn-red inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
            >
              Demander un devis
              <ArrowRight size={16} />
            </Link>
            <a
              href="#services"
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-semibold text-black border border-black/15 hover:bg-black hover:text-white transition"
            >
              Nos services
            </a>
          </div>
        </div>

        <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 border-t border-black/10 pt-10">
          {[
            { k: "7j/7", v: "8h — 20h" },
            { k: "< 24h", v: "Réponse devis" },
            { k: "Europe", v: "Zone d'intervention" },
            { k: "100%", v: "Devis gratuit" },
          ].map((s) => (
            <div key={s.k} data-testid={`hero-stat-${s.k}`}>
              <div className="font-display text-3xl md:text-4xl font-medium tracking-tight">{s.k}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-2">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute top-1/2 right-0 w-2 h-40 bg-[#FFB800] hidden lg:block" />
    </section>
  );
}
